import { z } from 'zod';
import { PUBLIC_LIMIT_TYPES, type PublicLimitType, type PublicSessionStatus } from '../enums.js';
import { maxCostForTime, type TouPeriod } from '../tou.js';

/** What guests can choose: an amount of money or a length of time. (Energy limits remain for older sessions.) */
export const GUEST_LIMIT_TYPES = ['money', 'time'] as const;
export type GuestLimitType = (typeof GUEST_LIMIT_TYPES)[number];

/** Quick picks on the first screen, in the units guests see: dollars and minutes. */
export const GUEST_PRESETS: Record<GuestLimitType, readonly number[]> = {
  money: [5, 10, 20, 30, 50],
  time: [5, 10, 30, 60, 90],
};

/** The slider's range and step, wider than the quick picks: whole dollars, whole minutes. */
export const GUEST_RANGE: Record<GuestLimitType, { min: number; max: number; step: number }> = {
  money: { min: 1, max: 100, step: 1 },
  time: { min: 2, max: 240, step: 1 },
};

/**
 * The most idle fee one guest session is charged, when the price doesn't set a maximum: this
 * many minutes' worth. It isn't held; it's charged to the guest's saved card after they unplug.
 */
export const IDLE_ALLOWANCE_MINUTES = 60;

/** The most idle fee one guest session is charged, cents: the price's maximum, else an hour's worth. 0 with no idle fee. */
export function guestIdleAllowanceCents(idleFeePerMinute: number | null, idleFeeMax: number | null): number {
  if (!idleFeePerMinute) return 0;
  return Math.round((idleFeeMax ?? idleFeePerMinute * IDLE_ALLOWANCE_MINUTES) * 100);
}
/** The smallest hold, and the smallest final amount worth taking (less is not charged). */
export const MIN_CHARGE_CENTS = 50;
/** For time-limited sessions on chargers with no rated power. */
export const DEFAULT_RATED_KW = 7.4;

/**
 * Each socket of a public charger has its own code, e.g. K7M-2QX: printed under its QR code
 * (/charge/K7M-2QX) and typed in when the QR code won't scan. Six random characters with no
 * look-alikes (0/O, 1/I), so it means nothing and can't be worked out. Stored without the dash.
 */
export const CHARGE_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const CHARGE_CODE_LENGTH = 6;

/** "k7m 2qx", "K7M-2QX" or "K7M2QX" -> "K7M2QX" (O and I typed by mistake read as 0 and 1 can't occur: they're not used). */
export const chargeCode = z
  .string()
  .transform((v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))
  .pipe(z.string().regex(new RegExp(`^[${CHARGE_CODE_ALPHABET}]{${CHARGE_CODE_LENGTH}}$`), 'Check the code: 6 letters and numbers, e.g. K7M-2QX'));

/** "K7M2QX" -> "K7M-2QX". */
export const formatChargeCode = (code: string) => `${code.slice(0, 3)}-${code.slice(3)}`;

/** Start a guest session: the socket's code and an amount within the range. */
export const guestStartBody = z
  .object({
    code: chargeCode,
    limitType: z.enum(GUEST_LIMIT_TYPES),
    /** Dollars or minutes. */
    amount: z.coerce.number().positive(),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().email('Check the email address').max(254)]).optional(),
  })
  .refine(
    (b) => {
      const r = GUEST_RANGE[b.limitType];
      return b.amount >= r.min && b.amount <= r.max && Number.isInteger(b.amount / r.step);
    },
    { message: 'Choose an amount within the range shown', path: ['amount'] },
  );
export type GuestStartBody = z.input<typeof guestStartBody>;

/**
 * Pay for a session: the token Square's Web Payments SDK made from the card, Apple Pay or
 * Google Pay in the browser. The server authorises the hold with it.
 */
export const guestPayBody = z.object({
  sourceId: z.string().trim().min(1).max(512),
  /** From buyer verification (3-D Secure), when the SDK asked for it. */
  verificationToken: z.string().trim().max(1024).optional(),
  /** How they paid, for the receipt. */
  wallet: z.enum(['Apple Pay', 'Google Pay']).optional(),
  /** The guest agreed to save the card for any idle fee. Required at chargers with idle fees (card only there). */
  saveCard: z.boolean().optional(),
});
export type GuestPayBody = z.input<typeof guestPayBody>;

export const receiptEmailBody = z.object({ email: z.string().trim().toLowerCase().email('Check the email address').max(254) });

export const refundBody = z.object({
  /** Cents to give back; leave out to refund everything not yet refunded. */
  amountCents: z.coerce.number().int().min(1).optional(),
});

export interface GuestPrice {
  /** $/kWh now. */
  pricePerKwh: number;
  idleFeePerMinute: number | null;
  /** The price's maximum idle fee a session, $ (null: an hour's worth). */
  idleFeeMax?: number | null;
  ratedKw: number | null;
  /** Time-of-use prices: each period's $/kWh, on the site's clock, from `at`. */
  periods?: { periods: TouPeriod[]; prices: number[]; timezone: string; at: Date };
}

/**
 * What a guest's choice costs and what we hold:
 *  - chargeCents: the most the energy can cost, at least MIN_CHARGE_CENTS (with peak and
 *    off-peak prices, a time limit is costed at each quarter hour's price; an energy limit at
 *    the highest),
 *  - holdCents: the same: only what they chose is held,
 *  - idleAllowanceCents: the most idle fee they can be charged (the price's maximum, else an hour's worth), if this
 *    charger has one. Not held: charged separately to their saved card after they unplug.
 * limitValue is what the charger is held to: Wh, cents or minutes.
 */
export function guestQuote(limitType: PublicLimitType, amount: number, p: GuestPrice) {
  let chargeCents: number;
  let limitValue: number;
  if (limitType === 'energy') {
    limitValue = Math.round(amount * 1000);
    chargeCents = Math.round(amount * Math.max(p.pricePerKwh, ...(p.periods?.prices ?? [])) * 100);
  } else if (limitType === 'money') {
    limitValue = Math.round(amount * 100);
    chargeCents = limitValue;
  } else {
    limitValue = Math.round(amount);
    const kw = p.ratedKw || DEFAULT_RATED_KW;
    chargeCents = p.periods
      ? maxCostForTime(p.periods.periods, p.periods.prices, p.periods.at, amount, kw, p.periods.timezone)
      : Math.round((amount / 60) * kw * p.pricePerKwh * 100);
  }
  chargeCents = Math.max(MIN_CHARGE_CENTS, chargeCents);
  const idleAllowanceCents = guestIdleAllowanceCents(p.idleFeePerMinute, p.idleFeeMax ?? null);
  return { limitValue, chargeCents, idleAllowanceCents, holdCents: chargeCents };
}

/**
 * Idle fee: per minute once the car has stopped charging and the free minutes are used,
 * until it's unplugged, at most the allowance kept in the hold.
 */
export function idleFeeCents(o: { idleStartedAt: Date | string | null; until: Date | string; graceMinutes: number; feePerMinute: number | null; allowanceCents: number }): number {
  if (!o.idleStartedAt || !o.feePerMinute || o.allowanceCents <= 0) return 0;
  const minutes = (new Date(o.until).getTime() - new Date(o.idleStartedAt).getTime()) / 60_000;
  const charged = Math.max(0, Math.floor(minutes - o.graceMinutes));
  return Math.min(o.allowanceCents, Math.round(charged * o.feePerMinute * 100));
}

export const GUEST_STATUS_LABELS: Record<PublicSessionStatus, string> = {
  created: 'Not paid',
  authorised: 'Paid, starting',
  charging: 'Charging',
  stopping: 'Stopping',
  completed: 'Finished, waiting to unplug',
  captured: 'Paid',
  cancelled: 'Cancelled, not charged',
  failed: 'Payment failed',
};
